import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { alpha, Box, Button, Chip, Divider, Paper, Stack, Typography } from '@mui/material';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';

import { DateFormatsEnum } from '../../../../constants/date';
import { LAST_RUN_VIEW_LABEL, RUN_CHECK_LABEL } from '../../../../constants/organizations';
import { getRedactedSendsLabel } from '../../../../constants/organizationsKeys';
import {
  IAnonymizationLastRunStatus,
  IRunCheckDateRange,
  LastRunViewEnum,
  RunAnonymizationCheckPhaseEnum,
} from '../../../../types/organizations';
import { detectUserTimezone, isValidDate } from '../../../../utils/date';
import { getLastRunView } from '../../../../utils/organizations';

import RunAnonymizationCheckDialog from './RunAnonymizationCheckDialog';

interface IProps {
  enabled: boolean;
  lastRun: IAnonymizationLastRunStatus | null;
  isRunCheckDisabled?: boolean;
  isRunCheckBusy?: boolean;
  runCheckPhase?: RunAnonymizationCheckPhaseEnum;
  onRunCheck?: (range: IRunCheckDateRange) => void;
}

const AnonymizationStatusCard: React.FC<IProps> = ({
  enabled,
  lastRun,
  isRunCheckDisabled,
  isRunCheckBusy = false,
  runCheckPhase = RunAnonymizationCheckPhaseEnum.Idle,
  onRunCheck,
}) => {
  const [isRunCheckDialogOpen, setIsRunCheckDialogOpen] = useState(false);

  const view = useMemo(() => getLastRunView(lastRun), [lastRun]);
  const isFailed = useMemo(() => view === LastRunViewEnum.Failed, [view]);

  const lastRunDate = useMemo(() => {
    const date = new Date(lastRun?.timestamp ?? '');

    if (!isValidDate(date)) {
      return '';
    }

    return format(toZonedTime(date, detectUserTimezone().utc[0]), DateFormatsEnum.ShortHumanReadable);
  }, [lastRun]);

  const details = useMemo(() => {
    switch (view) {
      case LastRunViewEnum.Completed:
        return getRedactedSendsLabel(lastRun?.number_sends ?? 0);
      case LastRunViewEnum.NoMatches:
        return 'No sends matched · nothing was changed';
      case LastRunViewEnum.Failed: {
        const numberSends = lastRun?.number_sends ?? 0;
        const outcome =
          numberSends > 0
            ? `Didn’t complete · ${getRedactedSendsLabel(numberSends)} before it stopped`
            : 'Didn’t complete, no data was changed';

        return enabled ? `${outcome} · retries at the next daily check` : outcome;
      }
      default:
        return '';
    }
  }, [view, lastRun, enabled]);

  const openRunCheckDialog = useCallback(() => setIsRunCheckDialogOpen(true), []);
  const closeRunCheckDialog = useCallback(() => setIsRunCheckDialogOpen(false), []);

  const handleRunCheckConfirm = useCallback(
    (range: IRunCheckDateRange) => {
      setIsRunCheckDialogOpen(false);
      onRunCheck?.(range);
    },
    [onRunCheck],
  );

  return (
    <Paper
      elevation={0}
      data-testid="anonymization-status-card"
      sx={{
        minWidth: 0,
        px: 3.75,
        py: 2.5,
        borderRadius: '10px',
        color: 'grey.900',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
        <Stack direction="row" alignItems="flex-start" gap={1.25}>
          <Box
            aria-hidden
            sx={{
              flexShrink: 0,
              width: 10,
              height: 10,
              mt: 1,
              borderRadius: '50%',
              bgcolor: enabled ? 'success.main' : 'grey.400',
            }}
          />
          <Stack direction="row" alignItems="center" flexWrap="wrap" columnGap={0.5} minHeight={26}>
            <Typography variant="body1" component="span" fontWeight={500}>
              {enabled ? 'On' : 'Off'}
            </Typography>
            <Typography variant="body1" component="span" color="text.secondary">
              · {enabled ? 'checked daily' : 'manual runs only'}
            </Typography>
          </Stack>
        </Stack>
        {onRunCheck && (
          <Button
            variant="outlined"
            size="small"
            onClick={openRunCheckDialog}
            disabled={isRunCheckDisabled}
            aria-busy={isRunCheckBusy}
            data-action="anonymization_run_now_button"
            sx={{
              minWidth: 132,
              px: 1.75,
              py: 1,
              justifyContent: 'center',
              border: 1,
              borderColor: 'primary.main',
              borderRadius: '10px',
              bgcolor: 'transparent',
              color: 'primary.main',
              fontSize: '0.875rem',
              lineHeight: 1.15,
              fontWeight: 500,
              whiteSpace: 'nowrap',
              boxShadow: 'none',
              transition: 'all 0.5s',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'transparent',
                boxShadow: '0 10px 20px rgba(0, 0, 0, 0.19), 0 6px 6px rgba(33, 33, 33, 0.23)',
              },
            }}
          >
            {RUN_CHECK_LABEL[runCheckPhase]}
          </Button>
        )}
      </Stack>
      <Divider sx={{ my: 2, borderColor: 'grey.100' }} />
      <Typography variant="body1" color="text.disabled" sx={{ mb: 1.25 }}>
        Last check
      </Typography>
      {view === LastRunViewEnum.None || !lastRun ? (
        <Typography variant="body1" sx={{ minHeight: 26 }}>
          {enabled ? 'No checks yet — the first one runs tonight' : 'No checks yet'}
        </Typography>
      ) : (
        <>
          <Stack direction="row" alignItems="center" flexWrap="wrap" columnGap={1.5} rowGap={1} minHeight={26}>
            <Typography variant="body1" component="span">
              {lastRunDate}
            </Typography>
            <Chip
              size="small"
              color={isFailed ? 'error' : 'success'}
              icon={isFailed ? <ErrorOutlineIcon /> : <CheckCircleOutlineIcon />}
              label={LAST_RUN_VIEW_LABEL[view]}
              sx={(theme) => {
                const palette = isFailed ? theme.palette.error : theme.palette.success;

                return {
                  '&&': {
                    height: 26,
                    gap: 0.75,
                    px: 1.25,
                    pl: 1,
                    borderRadius: '13px',
                    fontSize: '0.875rem',
                    lineHeight: 1,
                    fontWeight: 500,
                    color: palette.dark,
                    bgcolor: alpha(palette.main, 0.1),
                  },
                  '& .MuiChip-icon': { m: 0, color: 'inherit', fontSize: '1rem' },
                  '& .MuiChip-label': { p: 0 },
                };
              }}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
            {details}
          </Typography>
        </>
      )}
      {onRunCheck && (
        <RunAnonymizationCheckDialog
          open={isRunCheckDialogOpen}
          onClose={closeRunCheckDialog}
          onConfirm={handleRunCheckConfirm}
        />
      )}
    </Paper>
  );
};

export default AnonymizationStatusCard;
